import { Button } from '@/shared/components/ui/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { listSettings, putSetting } from '../api';

/**
 * Pesquisa de Satisfação — avaliação 1-5 pós-atendimento.
 *
 * Config em advanced_settings (`rating.*`). O gatilho por motivo de fechamento
 * (toggle "Enviar avaliação") fica na aba Motivos de Fechamento.
 *
 * Layout segue o padrão das outras abas: card único `border-n-200 shadow-sm`
 * com linhas divididas por `border-n-100`.
 * Defaults espelham RATING_DEFAULTS de packages/db/src/rating.ts.
 */
const DEFAULTS = {
  enabled: false,
  timeout_minutes: 0,
  msg_request:
    'Olá {{firstname}}! Como você avalia o atendimento que acabou de receber?\n' +
    'Responda com uma nota de *1* a *5*:\n\n' +
    '*5* - Excelente 🤩\n' +
    '*4* - Bom 😍\n' +
    '*3* - Neutro 😐\n' +
    '*2* - Ruim 🥺\n' +
    '*1* - Péssimo 😔',
  msg_thanks: 'Obrigado pela sua avaliação! 💙',
  msg_invalid: 'Por favor, responda com uma nota de *1* a *5* para avaliar o atendimento.',
  invalid_mode: 'warn_once' as 'warn_always' | 'warn_once' | 'release',
};

type FormState = typeof DEFAULTS;

export function AvaliacaoTab() {
  const qc = useQueryClient();
  const settingsQ = useQuery({
    queryKey: ['settings', 'rating-prefix'],
    queryFn: () => listSettings('rating.'),
  });

  const [form, setForm] = useState<FormState>(DEFAULTS);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!settingsQ.data) return;
    const kv = new Map(settingsQ.data.map((s) => [s.key, s.value]));
    setForm({
      enabled: kv.get('rating.enabled') === true,
      timeout_minutes:
        typeof kv.get('rating.timeout_minutes') === 'number'
          ? (kv.get('rating.timeout_minutes') as number)
          : DEFAULTS.timeout_minutes,
      msg_request:
        typeof kv.get('rating.msg_request') === 'string' && kv.get('rating.msg_request')
          ? (kv.get('rating.msg_request') as string)
          : DEFAULTS.msg_request,
      msg_thanks: kv.has('rating.msg_thanks')
        ? String(kv.get('rating.msg_thanks') ?? '')
        : DEFAULTS.msg_thanks,
      msg_invalid: kv.has('rating.msg_invalid')
        ? String(kv.get('rating.msg_invalid') ?? '')
        : DEFAULTS.msg_invalid,
      invalid_mode: ['warn_always', 'warn_once', 'release'].includes(
        String(kv.get('rating.invalid_mode')),
      )
        ? (kv.get('rating.invalid_mode') as FormState['invalid_mode'])
        : DEFAULTS.invalid_mode,
    });
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.dataUpdatedAt]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      await putSetting('rating.enabled', form.enabled);
      await putSetting('rating.timeout_minutes', form.timeout_minutes);
      await putSetting('rating.msg_request', form.msg_request);
      await putSetting('rating.msg_thanks', form.msg_thanks);
      await putSetting('rating.msg_invalid', form.msg_invalid);
      await putSetting('rating.invalid_mode', form.invalid_mode);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'rating-prefix'] });
      setDirty(false);
      toast.success('Pesquisa de Satisfação salva');
    },
    onError: () => toast.error('Não foi possível salvar', { description: 'Tente novamente.' }),
  });

  if (settingsQ.isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-[13px] text-n-500">
        <Loader2 size={16} className="animate-spin" /> Carregando…
      </div>
    );
  }

  return (
    <section>
      <header className="mb-5">
        <h2
          className="font-bold text-[20px] text-n-900"
          style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
        >
          Pesquisa de Satisfação
        </h2>
        <p className="text-[13px] text-n-500">
          Ao encerrar um atendimento, o cliente recebe um pedido de avaliação de 1 a 5. O disparo é
          por motivo de fechamento — marque "Enviar avaliação" nos motivos desejados na aba{' '}
          <strong>Motivos de Fechamento</strong>.
        </p>
      </header>

      <div className="rounded-md border border-n-200 bg-n-0 shadow-sm">
        {/* Habilitar — checkbox à esquerda com a pergunta ao lado (referência
            escolhida pelo dono). */}
        <label className="flex cursor-pointer items-center gap-2.5 border-b border-n-100 px-4 py-3.5">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
            className="h-[15px] w-[15px] flex-shrink-0 accent-brand-500"
          />
          <span className="font-semibold text-[13px] text-n-800">
            Habilitar Pesquisa de Satisfação do Atendimento?
          </span>
        </label>

        {/* Tempo limite — input à esquerda, nome ao lado */}
        <div className="flex flex-col gap-1 border-b border-n-100 px-4 py-3">
          <div className="flex items-center gap-3">
            <input
              id="rating-timeout"
              type="number"
              min={0}
              max={43200}
              value={String(form.timeout_minutes)}
              onChange={(e) => set('timeout_minutes', Math.max(0, Number(e.target.value) || 0))}
              className="w-[110px] rounded-md border border-n-200 px-3 py-2 text-[13px]"
            />
            <label className="font-semibold text-[13px] text-n-800" htmlFor="rating-timeout">
              Tempo limite para avaliação (minutos)
            </label>
          </div>
          <span className="text-[11px] text-n-500">
            Depois desse tempo a resposta do cliente volta a abrir atendimento normal. 0 = sem
            limite.
          </span>
        </div>

        {/* Mensagens */}
        <MsgRow
          label="Solicitar avaliação"
          hint={
            <>
              Enviada ao encerrar o atendimento. Aceita variáveis (ex:{' '}
              <code className="text-n-600">{'{{firstname}}'}</code>,{' '}
              <code className="text-n-600">{'{{name}}'}</code>,{' '}
              <code className="text-n-600">{'{{atendente}}'}</code>).
            </>
          }
          value={form.msg_request}
          rows={7}
          onChange={(v) => set('msg_request', v)}
        />
        <MsgRow
          label="Agradecimento pela avaliação"
          hint="Enviada quando o cliente dá a nota. Deixe em branco para não enviar."
          value={form.msg_thanks}
          onChange={(v) => set('msg_thanks', v)}
        />
        <MsgRow
          label="Erro ao informar uma avaliação inválida"
          hint="Enviada quando o cliente responde algo que não é uma nota de 1 a 5."
          value={form.msg_invalid}
          onChange={(v) => set('msg_invalid', v)}
        />

        {/* Modo de resposta inválida */}
        <div className="flex flex-col gap-1 px-4 py-3">
          <label className="font-semibold text-[13px] text-n-800" htmlFor="rating-mode">
            Comportamento com resposta que não é nota
          </label>
          <select
            id="rating-mode"
            value={form.invalid_mode}
            onChange={(e) => set('invalid_mode', e.target.value as FormState['invalid_mode'])}
            className="w-full max-w-[420px] rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px]"
          >
            <option value="warn_once">Avisar 1 vez, depois liberar a conversa (recomendado)</option>
            <option value="warn_always">Sempre avisar até avaliar ou estourar o tempo</option>
            <option value="release">Liberar direto, sem aviso</option>
          </select>
          <span className="text-[11px] text-n-500">
            "Liberar" = a mensagem do cliente segue o fluxo normal (reabre/cria atendimento) e a
            avaliação é cancelada.
          </span>
        </div>

        {/* Rodapé do card — Salvar dentro do box (pedido do dono) */}
        <div className="flex items-center justify-end gap-3 border-t border-n-100 bg-n-50/60 px-4 py-3">
          {dirty && <span className="text-[12px] text-n-500">Alterações não salvas</span>}
          <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
            {saveMut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </section>
  );
}

function MsgRow({
  label,
  hint,
  value,
  rows = 4,
  onChange,
}: {
  label: string;
  hint: React.ReactNode;
  value: string;
  rows?: number;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 border-b border-n-100 px-4 py-3">
      <span className="font-semibold text-[13px] text-n-800">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="resize-none rounded-md border border-n-200 px-3 py-2 text-[13px]"
      />
      <span className="text-[11px] text-n-500">{hint}</span>
    </div>
  );
}
